import React from 'react';
import CountUp from 'react-countup';
import { FaUsers, FaGraduationCap, FaTrophy, FaChalkboardTeacher, FaAward, FaBookOpen } from 'react-icons/fa';

const Counters = () => {
  const counterData = [
    {
      id: 1,
      icon: FaUsers,
      number: 1250,
      suffix: '+',
      label: 'Students Enrolled',
      description: 'Active students across all grade levels',
      color: 'blue'
    },
    {
      id: 2,
      icon: FaChalkboardTeacher,
      number: 85,
      suffix: '+',
      label: 'Qualified Teachers',
      description: 'Experienced educators with advanced degrees',
      color: 'green'
    },
    {
      id: 3,
      icon: FaGraduationCap,
      number: 2500,
      suffix: '+',
      label: 'Graduated Students',
      description: 'Successful alumni worldwide',
      color: 'purple'
    },
    {
      id: 4,
      icon: FaTrophy,
      number: 150,
      suffix: '+',
      label: 'Awards Won',
      description: 'Academic and sports achievements',
      color: 'orange'
    },
    {
      id: 5,
      icon: FaAward,
      number: 98,
      suffix: '%',
      label: 'College Acceptance',
      description: 'Students accepted to top universities',
      color: 'red'
    },
    {
      id: 6,
      icon: FaBookOpen,
      number: 25,
      suffix: '+',
      label: 'Years of Excellence',
      description: 'Dedicated to quality education',
      color: 'indigo'
    }
  ];

  const getColorClasses = (color) => {
    const colors = {
      blue: {
        icon: 'text-blue-600 dark:text-blue-400',
        bg: 'bg-blue-100 dark:bg-blue-900',
        number: 'text-blue-600 dark:text-blue-400'
      },
      green: {
        icon: 'text-green-600 dark:text-green-400',
        bg: 'bg-green-100 dark:bg-green-900',
        number: 'text-green-600 dark:text-green-400'
      },
      purple: {
        icon: 'text-purple-600 dark:text-purple-400',
        bg: 'bg-purple-100 dark:bg-purple-900',
        number: 'text-purple-600 dark:text-purple-400'
      },
      orange: {
        icon: 'text-orange-600 dark:text-orange-400',
        bg: 'bg-orange-100 dark:bg-orange-900',
        number: 'text-orange-600 dark:text-orange-400'
      },
      red: {
        icon: 'text-red-600 dark:text-red-400',
        bg: 'bg-red-100 dark:bg-red-900',
        number: 'text-red-600 dark:text-red-400'
      },
      indigo: {
        icon: 'text-indigo-600 dark:text-indigo-400',
        bg: 'bg-indigo-100 dark:bg-indigo-900',
        number: 'text-indigo-600 dark:text-indigo-400'
      }
    };
    return colors[color] || colors.blue;
  };

  return (
    <section className="py-20 bg-gray-50 dark:bg-gray-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16" data-aos="fade-up">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
            Our Impact in Numbers
          </h2>
          <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
            Discover the impressive statistics that reflect our commitment to excellence in education
          </p>
        </div>

        {/* Counters Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {counterData.map((counter, index) => {
            const IconComponent = counter.icon;
            const colorClasses = getColorClasses(counter.color);
            
            return (
              <div
                key={counter.id}
                className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-8 text-center hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2"
                data-aos="zoom-in"
                data-aos-delay={index * 100}
              >
                {/* Icon */}
                <div className={`w-16 h-16 ${colorClasses.bg} rounded-full flex items-center justify-center mx-auto mb-6`}>
                  <IconComponent className={`w-8 h-8 ${colorClasses.icon}`} />
                </div>

                {/* Counter */}
                <div className="mb-4">
                  <div className={`text-4xl md:text-5xl font-bold ${colorClasses.number} mb-2`}>
                    <CountUp
                      end={counter.number}
                      suffix={counter.suffix}
                      duration={2.5}
                      delay={0.5}
                      enableScrollSpy={true}
                      scrollSpyOnce={true}
                    />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                    {counter.label}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-300 text-sm">
                    {counter.description}
                  </p>
                </div>
              </div>
            );
          })}
        </div>

        {/* Additional Stats */}
        <div className="mt-16 grid grid-cols-1 md:grid-cols-4 gap-8" data-aos="fade-up">
          <div className="text-center">
            <div className="text-2xl md:text-3xl font-bold text-blue-600 dark:text-blue-400 mb-2">
              15:1
            </div>
            <div className="text-gray-600 dark:text-gray-300 text-sm">
              Student-Teacher Ratio
            </div>
          </div>
          <div className="text-center">
            <div className="text-2xl md:text-3xl font-bold text-green-600 dark:text-green-400 mb-2">
              95%
            </div>
            <div className="text-gray-600 dark:text-gray-300 text-sm">
              Parent Satisfaction
            </div>
          </div>
          <div className="text-center">
            <div className="text-2xl md:text-3xl font-bold text-purple-600 dark:text-purple-400 mb-2">
              50+
            </div>
            <div className="text-gray-600 dark:text-gray-300 text-sm">
              Extracurricular Activities
            </div>
          </div>
          <div className="text-center">
            <div className="text-2xl md:text-3xl font-bold text-orange-600 dark:text-orange-400 mb-2">
              100%
            </div>
            <div className="text-gray-600 dark:text-gray-300 text-sm">
              Technology Integration
            </div>
          </div>
        </div>

        {/* Call to Action */}
        <div className="mt-16 text-center" data-aos="fade-up">
          <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
            Be Part of Our Success Story
          </h3>
          <p className="text-gray-600 dark:text-gray-300 mb-8 max-w-2xl mx-auto">
            Join thousands of families who have chosen Rasoha Academy for their children's education.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button className="bg-blue-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-all duration-300 transform hover:scale-105">
              Learn More
            </button>
            <button className="border-2 border-blue-600 text-blue-600 dark:text-blue-400 px-8 py-3 rounded-lg font-semibold hover:bg-blue-600 hover:text-white transition-all duration-300 transform hover:scale-105">
              Contact Us
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Counters; 